import React, { useState, ChangeEvent, useEffect } from 'react';
import {
  Grid,
  Typography,
  FormControlLabel,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  FormHelperText,
} from '@material-ui/core';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useStyles } from './ExamResultsRepository.styles';
import { useLayoutContext } from '../../Context/Layout.context';
import { ModalWithButtons } from '../ModalWithButtons/ModalWithButtons.component';
import { BackendErrorCode, ExamRepositoryModalType, ExamResultsFileBackendErrorCode, ExaminerIdType, FileType } from './ExamResultsRepository.types';
import HideSourceIcon from '@mui/icons-material/HideSource';
import { SourceSystemType } from '../../models/examSessionDto.types';
import { exportExamDocumentation, exportExamResults } from '../../services/exams/sessions/sessions.service';
import { exportFile } from '../../utils/servicesHelper';
import { CheckboxOne } from '../CheckboxOne/CheckboxOne.component';
import { useTranslation } from 'react-i18next';

interface ExamResultsRepositoryProps {
  sourceSystem?: SourceSystemType;
  sheetCode?: string;
  examSheetId?: string;
  type: ExamRepositoryModalType;
  examineeId?: string;
  onClose: () => void;
}

export const ExamResultsRepository: React.FC<ExamResultsRepositoryProps> = ({
  sourceSystem,
  sheetCode,
  examSheetId,
  type,
  examineeId,
  onClose,
}) => {
  const classes = useStyles();

  const { showLoader, openAlert } = useLayoutContext();
  const { t } = useTranslation();

  const [pesel, setPesel] = useState<string | undefined>(examineeId ?? undefined);
  const [schoolId, setSchoolId] = useState<string | undefined>();
  const [fileType, setFileType] = useState<FileType>(FileType.csv);
  const [examinerIdType, setexaminerIdType] = useState<ExaminerIdType>(ExaminerIdType.pesel);
  const [errorCode, setErrorCode] = useState<BackendErrorCode | ExamResultsFileBackendErrorCode | undefined>();
  const [isAnnotationOnPdf, setIsAnnotationOpPdf] = useState<boolean>(true);
  const [isScoringAfterEachExamOnPdf, setIsScoringAfterEachExamOnPdf] = useState<boolean>(true);
  const [isExaminerDataOnPdf, setIsExaminerDataOnPdf] = useState<boolean>(true);

  const confirmationButtonLabel = type === ExamRepositoryModalType.results ? 'Pobierz plik' : 'Wygeneruj PDF';
  const title = type === ExamRepositoryModalType.results ? `Pobierz wyniki` : `Dokumentacja (${sheetCode})`;

  const handleSchoolIdChange = (value: string) => {
    setSchoolId(value);
    setErrorCode(undefined);
  };

  const handleExaminerIdTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setexaminerIdType(event.target.value as ExaminerIdType);
    setSchoolId('');
    setPesel('');
    setErrorCode(undefined);
  };

  const handlePeselChange = (value: string) => {
    setPesel(value);
    setErrorCode(undefined);
  };

  const typesStyle = {
    '&.Mui-checked': {
      color: '#3C419A',
    },
  };

  const getErrorMessage = (errorCode: BackendErrorCode) => {
    switch (errorCode) {
      case BackendErrorCode.InvalidSchoolCode:
        return 'Błędny identyfikator szkoły';
      case BackendErrorCode.InvalidExamineeIdentity:
        return 'Błędny PESEL zdającego';
    }
  };

  const isWrongPesel = () => {
    return errorCode === BackendErrorCode.InvalidExamineeIdentity || errorCode === ExamResultsFileBackendErrorCode.NoExaminee;
  };

  const isWrongSchoolId = () => {
    return errorCode === BackendErrorCode.InvalidSchoolCode || errorCode === ExamResultsFileBackendErrorCode.NoSchool;
  };

  const isConfirmationButtonDisabled = () => {
    return isWrongPesel() || isWrongSchoolId() || (!pesel && !schoolId);
  };

  const handleExportResults = () => {
    if (examSheetId && sheetCode) {
      showLoader(true);
      exportExamResults(examSheetId, schoolId, pesel, fileType)
        .then(
          (response: Blob) => {
            if (response) {
              exportFile(response, sheetCode.toString(), fileType);
              openAlert('success', 'Przyjęto zlecenie wygenerowania dokumentacji egzaminacyjnej');
              onClose();
            } else openAlert('error', 'Wystąpił błąd podczas generowania dokumentacji. Spróbuj ponownie później');
          },
          (errors) => {
            if (!navigator.onLine) {
              openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
            } else if (errors.response.data === ExamResultsFileBackendErrorCode.NonClosedAssessmentSessions) {
              setErrorCode(ExamResultsFileBackendErrorCode.NonClosedAssessmentSessions);
            }
            if (errors.response.data === ExamResultsFileBackendErrorCode.NoExaminee) {
              setErrorCode(ExamResultsFileBackendErrorCode.NoExaminee);
            }
            if (errors.response.data === ExamResultsFileBackendErrorCode.NoSchool) {
              setErrorCode(ExamResultsFileBackendErrorCode.NoSchool);
            } else {
              openAlert('error', 'Wystąpił błąd podczas generowania dokumentacji. Spróbuj ponownie później');
            }
            openAlert('error', 'Wystąpił błąd podczas generowania dokumentacji. Spróbuj ponownie później');
          },
        )
        .finally(() => showLoader(false));
    }
  };

  const handleExportDocumentation = () => {
    if (sourceSystem && sheetCode && examSheetId) {
      showLoader(true);
      exportExamDocumentation(
        isAnnotationOnPdf,
        isScoringAfterEachExamOnPdf,
        isExaminerDataOnPdf,
        sheetCode,
        examSheetId,
        pesel ? pesel : null,
        schoolId ? schoolId : null,
        sourceSystem === (SourceSystemType[SourceSystemType.SIOEPKZ] as unknown),
      )
        .then(
          (response: BackendErrorCode) => {
            if (response === BackendErrorCode.InvalidExamineeIdentity) {
              setErrorCode(BackendErrorCode.InvalidExamineeIdentity);
            }
            if (response === BackendErrorCode.InvalidSchoolCode) {
              setErrorCode(BackendErrorCode.InvalidSchoolCode);
            }
            if (response === BackendErrorCode.Success) {
              openAlert('success', 'Przyjęto zlecenie wygenerowania dokumentacji egzaminacyjnej');
              onClose();
            }
            if (response === BackendErrorCode.Failure) {
              openAlert('error', 'Wystąpił błąd podczas generowania dokumentacji. Spróbuj ponownie później');
              onClose();
            }
          },
          () => {
            if (!navigator.onLine) {
              openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
            } else {
              openAlert('error', 'Wystąpił błąd podczas generowania dokumentacji. Spróbuj ponownie później');
            }
            onClose();
          },
        )
        .finally(() => showLoader(false));
    }
  };

  return (
    <>
      <ModalWithButtons
        xCloseButton={onClose}
        open={true}
        size="sm"
        title={title}
        autoHeight
        confirmButtonLabel={confirmationButtonLabel}
        onConfirm={type === ExamRepositoryModalType.results ? handleExportResults : handleExportDocumentation}
        confirmButtonDisabled={isConfirmationButtonDisabled()}
        onCancel={onClose}
        handleClose={onClose}
      >
        {type !== ExamRepositoryModalType.examineeDocumentation && (
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Wprowadź dane</Typography>
          </Grid>
        )}
        <Grid item xs={12} className={classes.item}>
          <RadioGroup
            row
            aria-label="wybór typu identyfikatora"
            name="id-type-selector"
            value={examinerIdType}
            onChange={handleExaminerIdTypeChange}
          >
            <>
              <Grid item xs={6} className={[classes.item, classes.itemIdTypeRow].join(' ')}>
                <Radio
                  value={ExaminerIdType.pesel}
                  sx={typesStyle}
                  disabled={type === ExamRepositoryModalType.examineeDocumentation}
                />
                <FormControl
                  variant="outlined"
                  focused={
                    type !== ExamRepositoryModalType.examineeDocumentation
                      ? examinerIdType === ExaminerIdType.pesel
                      : false
                  }
                  style={{ marginRight: '25px', position: 'relative', top: '20px' }}
                >
                  <InputLabel style={{ color: isWrongPesel() ? '#BC0000' : '#2C2D3E' }} htmlFor="pesel">
                    ID zdającego
                  </InputLabel>
                  <OutlinedInput
                    value={pesel}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handlePeselChange(e.target.value.trim())}
                    id="pesel"
                    label="ID zdającego"
                    placeholder="ID zdającego"
                    disabled={
                      examinerIdType === ExaminerIdType.schoolId ||
                      type === ExamRepositoryModalType.examineeDocumentation
                    }
                    error={isWrongPesel()}
                    inputProps={{
                      style: {
                        color: '#2C2D3E',
                      },
                    }}
                    endAdornment={
                      examinerIdType === ExaminerIdType.schoolId ? (
                        <InputAdornment position="end">
                          <HideSourceIcon fontSize="small" />
                        </InputAdornment>
                      ) : (
                        <div style={{ width: '20px' }}></div>
                      )
                    }
                  />
                  <div className={classes.helperRow}>
                    <FormHelperText error id="wrong-pesel-error" component={'div'}>
                      {isWrongPesel() && (
                        <div className={classes.errorMessage}>
                          {getErrorMessage(BackendErrorCode.InvalidExamineeIdentity)}
                        </div>
                      )}
                    </FormHelperText>
                  </div>
                </FormControl>
              </Grid>
              <Grid item xs={6} className={[classes.item, classes.itemIdTypeRow].join(' ')}>
                <Radio
                  value={ExaminerIdType.schoolId}
                  sx={typesStyle}
                  disabled={type === ExamRepositoryModalType.examineeDocumentation}
                />
                <FormControl
                  variant="outlined"
                  focused={examinerIdType === ExaminerIdType.schoolId}
                  style={{ marginRight: '10px', position: 'relative', top: '20px' }}
                >
                  <InputLabel style={{ color: isWrongSchoolId() ? '#BC0000' : '#2C2D3E' }} htmlFor="schoolId">
                    ID szkoły
                  </InputLabel>
                  <OutlinedInput
                    value={schoolId}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleSchoolIdChange(e.target.value.trim())}
                    id="schoolId"
                    label="ID szkoły"
                    placeholder="ID szkoły"
                    disabled={
                      examinerIdType === ExaminerIdType.pesel || type === ExamRepositoryModalType.examineeDocumentation
                    }
                    error={isWrongSchoolId()}
                    inputProps={{
                      style: {
                        color: '#2C2D3E',
                      },
                    }}
                    endAdornment={
                      examinerIdType === ExaminerIdType.pesel ? (
                        <InputAdornment position="end">
                          <HideSourceIcon fontSize="small" />
                        </InputAdornment>
                      ) : (
                        <div style={{ width: '30px' }}></div>
                      )
                    }
                  />
                  <div className={classes.helperRow}>
                    <FormHelperText error id="wrong-schoolId-error" component={'div'}>
                      {isWrongSchoolId() && (
                        <div className={classes.errorMessage}>
                          {getErrorMessage(BackendErrorCode.InvalidSchoolCode)}
                        </div>
                      )}
                    </FormHelperText>
                  </div>
                </FormControl>
              </Grid>
            </>
          </RadioGroup>
        </Grid>
        {type === ExamRepositoryModalType.results && (
          <>
            <Grid item xs={12}>
              <Typography className={classes.subTitle}>Wybierz format pliku do eksportu</Typography>
            </Grid>
            <Grid item xs={6} className={[classes.item, classes.itemFileTypeRow].join(' ')}>
              <RadioGroup
                row
                aria-label="wybór typu pliku"
                name="file-type-selector"
                value={fileType}
                onChange={(e) => {
                  setFileType(e.target.value as FileType);
                }}
              >
                <FormControlLabel
                  style={{ marginRight: '30px' }}
                  value={FileType.json}
                  control={<Radio sx={typesStyle} />}
                  label={FileType.json}
                />
                <FormControlLabel value={FileType.csv} control={<Radio sx={typesStyle} />} label={FileType.csv} />
              </RadioGroup>
            </Grid>
          </>
        )}

        {(type === ExamRepositoryModalType.documentation || type === ExamRepositoryModalType.examineeDocumentation) && (
          <>
            <Grid item xs={12}>
              <Typography className={classes.subTitle}>Wybierz zakres dodatkowych danych do wydruku</Typography>
            </Grid>
            <Grid item xs={12} className={classes.itemsPdfOptions}>
              <FormControlLabel
                disabled={isConfirmationButtonDisabled()}
                control={
                  <CheckboxOne
                    disabled={isConfirmationButtonDisabled()}
                    name={'Annotations'}
                    onChange={() => setIsAnnotationOpPdf(!isAnnotationOnPdf)}
                    checked={isAnnotationOnPdf}
                  />
                }
                label={<div className={classes.label}>Wyświetlaj adnotacje na obrazach rozwiązań</div>}
              />
              <FormControlLabel
                disabled={isConfirmationButtonDisabled()}
                control={
                  <CheckboxOne
                    disabled={isConfirmationButtonDisabled()}
                    name={'Scoring'}
                    onChange={() => setIsScoringAfterEachExamOnPdf(!isScoringAfterEachExamOnPdf)}
                    checked={isScoringAfterEachExamOnPdf}
                  />
                }
                label={
                  <div className={classes.label}>
                    Drukuj zestawienia z punktację poszczególnych kryteriów po każdym wydrukowanym egzaminie zdającego
                  </div>
                }
              />
              <FormControlLabel
                disabled={isConfirmationButtonDisabled()}
                control={
                  <CheckboxOne
                    disabled={isConfirmationButtonDisabled()}
                    name={'ExaminerData'}
                    onChange={() => setIsExaminerDataOnPdf(!isExaminerDataOnPdf)}
                    checked={isExaminerDataOnPdf}
                  />
                }
                label={
                  <div className={classes.label}>
                    {`Drukuj numer ${t('global:shortNames.OKE')} egzaminatora, nazwisko i imię oraz nr GZ, którą oceniał egzaminator`}
                  </div>
                }
              />
            </Grid>
          </>
        )}
      </ModalWithButtons>
    </>
  );
};
